import { Injectable } from '@angular/core';
import { mapToVoid } from '@examdojo/core/rxjs';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { finalize, map, tap } from 'rxjs';
import { ClassStudentHttpService } from './class-student-http.service';
import { ClassStudentStoreModel } from './class-student.model';
import { ClassStudentStore } from './class-student.store';

@Injectable({ providedIn: 'root' })
export class ClassStudentService {
  constructor(
    private readonly store: ClassStudentStore,
    private readonly http: ClassStudentHttpService,
    private readonly errorHandler: ErrorHandlerService,
  ) {}

  fetchByClassId(
    classId: string,
    { resetStore, showLoading }: { resetStore?: boolean; showLoading?: boolean } = { resetStore: false },
  ) {
    if (resetStore) {
      this.store.reset();
    }

    if (resetStore || showLoading) {
      this.store.setLoading(true);
    }

    return this.http.fetchByClassId(classId).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_student', action: 'fetch' }),
      map((students) => students.map((s) => this.mapToStoreModel(s))),
      tap((students) => (resetStore ? this.store.set(students) : this.store.upsertMany(students))),
      finalize(() => {
        if (resetStore || showLoading) {
          this.store.setLoading(false);
        }
      }),
    );
  }

  removeStudent(classId: string, studentId: string) {
    return this.http.removeStudent(classId, studentId).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_student', action: 'delete' }),
      tap(() => this.store.delete(this.buildId(classId, studentId))),
      mapToVoid(),
    );
  }

  resetStore() {
    this.store.reset();
  }

  private buildId(classId: string, studentId: string): string {
    return `${classId}__${studentId}`;
  }

  private mapToStoreModel(httpModel: Omit<ClassStudentStoreModel, 'id'>): ClassStudentStoreModel {
    return {
      ...httpModel,
      id: this.buildId(httpModel.class_id!, httpModel.student_id!),
    };
  }
}
